import { type ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import type { CapiNews, FeatureListItem } from 'types/capi/news';

import type { Crumb } from '~/components/BreadCrumbs';
import Image from '~/components/Image';
import Link from '~/components/Link';
import PageHeader from '~/components/PageHeader';
import Pill from '~/components/Pill';
import ArticleCardInteractionEvent from '~/events/ArticleCardInteractionEvent';
import useArticleCardImpression from '~/hooks/useArticleCardImpression';
import usePageData from '~/hooks/usePageData';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';
import useStructuredData from '~/hooks/useStructuredData';
import useUser from '~/hooks/useUser';
import Pagination from '~/routes/plps/components/Products/Pagination';
import { formatArticleListItemData } from '~/utils/dates';
import type { Region } from '~/utils/region';
import { appendTrailingSlash } from '~/utils/trailingSlash';

import NewsHub from './NewsHub';

export const FEED_MODULE_TYPES = {
  authorArchive: 'author-archive-list',
  search: 'news-search-results'
};

function FeedArticleLink({
  article,
  children,
  className,
  moduleType,
  position
}: {
  article: FeatureListItem;
  children: ReactNode;
  className?: string;
  moduleType: string;
  position: number;
}) {
  const { isBot } = useRootData();
  const user = useUser();
  const impressionRef = useArticleCardImpression<HTMLAnchorElement>({
    articleId: article.id,
    moduleType,
    position
  });

  return (
    <Link
      className={className}
      onClick={() => {
        new ArticleCardInteractionEvent({
          articleId: article.id,
          gesture: 'click',
          guestId: user.guestId,
          isBot,
          moduleType,
          position
        }).send();
      }}
      ref={impressionRef}
      to={article.path}
    >
      {children}
    </Link>
  );
}

type NewsSection = { label: string; nonGendered?: boolean; value: string };
type NewsGender = { label: string; value: string };

export const NEWS_SECTIONS: NewsSection[] = [
  { label: 'All', value: '' },
  { label: 'Features', nonGendered: true, value: 'features' },
  { label: 'Footwear', value: 'footwear' },
  { label: 'Clothing', value: 'clothing' },
  { label: 'Lifestyle', nonGendered: true, value: 'lifestyle' },
  { label: 'Size guides', nonGendered: true, value: 'size-guides' }
];

export const NEWS_GENDERS: NewsGender[] = [
  { label: 'All', value: '' },
  { label: 'Men', value: 'mens' },
  { label: 'Women', value: 'womens' }
];

function buildNewsPath(genderValue: string, sectionValue: string) {
  const segments = [genderValue, 'news', sectionValue].filter(Boolean);

  return `/${segments.join('/')}/`;
}

function findSection(segments: string[]) {
  return (
    NEWS_SECTIONS.find(
      (section) => section.value && segments.includes(section.value)
    ) ?? NEWS_SECTIONS[0]!
  );
}

function findGender(segments: string[]) {
  return (
    NEWS_GENDERS.find(
      (gender) => gender.value && segments.includes(gender.value)
    ) ?? NEWS_GENDERS[0]!
  );
}

function parsePage(raw: string | null) {
  const page = Number(raw);

  return Number.isInteger(page) && page > 0 ? page : 1;
}

export default function NewsFeedView() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const region = useRegion();
  const { _schemas, articles, feature, features, pagination, title } =
    usePageData<CapiNews>();

  const searchTerm = searchParams.get('q')?.trim();
  const isFirstPage = parsePage(searchParams.get('page')) === 1;

  const segments = appendTrailingSlash(pathname).split('/').filter(Boolean);
  const section = findSection(segments);
  const gender = findGender(segments);
  const isIndex = !section.value && !gender.value;

  const crumbs: Crumb[] = isIndex
    ? [{ label: 'Home', path: '/' }, { label: 'News' }]
    : [
        { label: 'Home', path: '/' },
        { label: 'News', path: '/news/' },
        { label: title }
      ];

  if (searchTerm) {
    return (
      <div className="flex min-h-[80vh] flex-col pb-12">
        {_schemas ? <StructuredData schemas={_schemas} /> : null}
        <PageHeader breadcrumbs={crumbs} title={title} />
        <div className="content-width flex flex-1 flex-col gap-12 py-8">
          {features?.length ? (
            <ArticleFeedList
              features={features}
              moduleType={FEED_MODULE_TYPES.search}
              region={region}
            />
          ) : (
            <NewsSearchEmpty searchTerm={searchTerm} />
          )}
          {pagination.pages ? <Pagination pagination={pagination} /> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-grey-tile pb-12">
      {_schemas ? <StructuredData schemas={_schemas} /> : null}
      <PageHeader
        actions={<NewsFilters gender={gender} section={section} />}
        breadcrumbs={crumbs}
        title={title}
      />
      <NewsHub
        articles={articles ?? []}
        feature={feature}
        isFirstPage={isFirstPage}
      />
      {pagination.pages ? (
        <div className="content-width">
          <Pagination pagination={pagination} />
        </div>
      ) : null}
    </div>
  );
}

function NewsSearchEmpty({ searchTerm }: { searchTerm: string }) {
  return (
    <p className="my-auto text-center text-light-grey">
      {`No articles found for “${searchTerm}”.`}
    </p>
  );
}

export function StructuredData({ schemas }: { schemas: object[] }) {
  useStructuredData(schemas);

  return null;
}

type FilterOption = { active: boolean; label: string; to: string };

function NewsFilters({
  gender,
  section
}: {
  gender: NewsGender;
  section: NewsSection;
}) {
  const activeGender = section.nonGendered ? '' : gender.value;

  const categoryOptions: FilterOption[] = NEWS_SECTIONS.map((option) => ({
    active: option.value === section.value,
    label: option.label,
    to: buildNewsPath(option.nonGendered ? '' : activeGender, option.value)
  }));

  return (
    <div className="no-scrollbar flex h-10 items-center gap-2 overflow-x-auto max-md:-mx-4 max-md:px-4 md:justify-end">
      {categoryOptions.map(({ active, label, to }) => (
        <Pill active={active} key={to} to={to}>
          {label}
        </Pill>
      ))}
    </div>
  );
}

export function ArticleFeedList({
  features,
  moduleType,
  region
}: {
  features: FeatureListItem[];
  moduleType: string;
  region: Region;
}) {
  if (!features.length) return null;

  return (
    <div className="grid grid-cols-3 gap-8">
      {features.map((feature, index) => (
        <FeedArticleLink
          article={feature}
          className="col-span-3 flex flex-col gap-3 md:flex-row lg:col-span-2 lg:gap-5"
          key={`${feature.title}-${index}`}
          moduleType={moduleType}
          position={index + 1}
        >
          <Image
            className="aspect-[16/9] w-full rounded-md object-cover md:w-[370px]"
            alt={feature.image?.alt}
            src={feature.image?.url ?? ''}
            pad={false}
            loading="lazy"
            responsiveOptions={{
              xs: { width: 576 }
            }}
            progressiveLoadSettings={{
              highResQuality: 100,
              lowResQuality: 50
            }}
          />
          <div className="flex flex-col gap-1">
            <div className="flex flex-col gap-1">
              <h3 className="text-title-sm line-clamp-3">{feature.title}</h3>
              {feature.excerpt && (
                <p className="line-clamp-3 hidden text-sm md:block">
                  {feature.excerpt}
                </p>
              )}
            </div>
            <p className="flex gap-2 text-sm text-grey-hide">
              <span>
                {formatArticleListItemData(feature.dates.published, region)}
              </span>
              {feature.author?.name ? (
                <>
                  <span>•</span>
                  <span>{feature.author.name}</span>
                </>
              ) : (
                <></>
              )}
            </p>
          </div>
        </FeedArticleLink>
      ))}
    </div>
  );
}
